/**
 * Modal for approving, rejecting, or approve-and-moving pending pack-quantity requests.
 */

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";

import { listPendingTransferMoveTargets } from "./transfersMoveTargets";
import { transferOrderPackedQuantityByItemId, type TransferBoardOrder } from "./transfersWorkbenchTypes";

type MoveTargetMode = "existing" | "new";

export function TransferPackingQuantityApprovalsModal({
  order,
  allOrders,
  hubWritesAllowed,
  onClose,
}: {
  order: TransferBoardOrder | null;
  allOrders: readonly TransferBoardOrder[];
  hubWritesAllowed: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [moveDialogRequestId, setMoveDialogRequestId] = useState<string | null>(null);
  const [moveTargetMode, setMoveTargetMode] = useState<MoveTargetMode>("existing");
  const [targetTransferOrderId, setTargetTransferOrderId] = useState("");
  const [shipByDate, setShipByDate] = useState("");
  const [newTransferNotes, setNewTransferNotes] = useState("");

  const approveMutation = useMutation<
    { request: { id: string; status: "approved" } },
    ApiError,
    string
  >({
    mutationFn: (requestId) =>
      apiFetch<{ request: { id: string; status: "approved" } }>(
        `/api/manufacturing/transfers/${String(order!.id)}/packing-quantity-requests/${requestId}/approve`,
        { method: "POST", body: {} },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "transfers-board"] });
      showToast({ tone: "success", message: "Pack quantity discrepancy approved." });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  const approveAndMoveMutation = useMutation<
    {
      request: {
        moved_to_transfer_order_id: number;
        moved_to_transfer_number: string | null;
        moved_quantity: number;
      };
    },
    ApiError,
    string
  >({
    mutationFn: (requestId) =>
      apiFetch(
        `/api/manufacturing/transfers/${String(order!.id)}/packing-quantity-requests/${requestId}/approve-and-move`,
        {
          method: "POST",
          body: {
            ...(moveTargetMode === "existing" && targetTransferOrderId !== ""
              ? { target_transfer_order_id: Number.parseInt(targetTransferOrderId, 10) }
              : {}),
            ...(moveTargetMode === "new" ? { ship_by_date: shipByDate, notes: newTransferNotes || null } : {}),
          },
        },
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "transfers-board"] });
      setMoveDialogRequestId(null);
      const target =
        result.request.moved_to_transfer_number
        ?? `Transfer #${String(result.request.moved_to_transfer_order_id)}`;
      showToast({
        tone: "success",
        message: `Approved and moved ${String(result.request.moved_quantity)} units to ${target}.`,
      });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  const rejectMutation = useMutation<
    { request: { id: string; status: "rejected" } },
    ApiError,
    { requestId: string; note: string | null }
  >({
    mutationFn: ({ requestId, note }) =>
      apiFetch<{ request: { id: string; status: "rejected" } }>(
        `/api/manufacturing/transfers/${String(order!.id)}/packing-quantity-requests/${requestId}/reject`,
        { method: "POST", body: { note } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "transfers-board"] });
      showToast({ tone: "success", message: "Pack quantity discrepancy rejected." });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  const packedByItemId = useMemo(
    () => (order != null ? transferOrderPackedQuantityByItemId(order) : new Map<number, number>()),
    [order],
  );

  const moveTargetOrders = useMemo(() => {
    if (order == null) return [];
    return listPendingTransferMoveTargets(
      allOrders,
      order.id,
      order.origin_location_id,
      order.destination_location_id,
    );
  }, [allOrders, order]);

  if (order == null) {
    return null;
  }

  const pendingRequests = (order.packing_quantity_requests ?? []).filter((row) => row.status === "pending");
  if (pendingRequests.length === 0) {
    return null;
  }

  const title = order.transfer_number ?? `Transfer #${String(order.id)}`;
  const mutationPending =
    approveMutation.isPending || rejectMutation.isPending || approveAndMoveMutation.isPending;

  const moveRequest = pendingRequests.find((row) => row.id === moveDialogRequestId) ?? null;

  return (
    <>
      <div
        className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="transfer-pack-qty-approvals-title"
        onClick={onClose}
      >
        <div
          className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 id="transfer-pack-qty-approvals-title" className="text-lg font-semibold text-slate-900">
              Pack quantity approvals — {title}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              This queue is transfer quantity only. Found inventory is a separate inventory discrepancy
              request; both can be pending while picking or packing continues. Approve ships the requested
              quantity. Approve and move also places the remainder on another pending transfer on the same
              lane. Reject unwinds hub pick and pack quantities back to the ordered line quantity.
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="border-b border-slate-200 px-2 py-2">Line</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Ordered</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">On hand</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Packed</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Requested</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Remainder</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Legacy inventory plus</th>
                  <th className="border-b border-slate-200 px-2 py-2">Submitted by</th>
                  <th className="border-b border-slate-200 px-2 py-2"> </th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((request) => {
                  const line = order.lines.find((entry) => entry.id === request.transfer_order_item_id);
                  const orderedQuantity = line?.quantity ?? request.ordered_quantity;
                  const packedQuantity = packedByItemId.get(request.transfer_order_item_id) ?? 0;
                  const remainder = orderedQuantity - request.requested_quantity;
                  const canMove = remainder > 0 && request.requested_quantity < orderedQuantity;
                  return (
                    <tr key={request.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-2 font-mono text-xs text-slate-900">
                        {line?.sku_id ?? `Line ${String(request.transfer_order_item_id)}`}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{orderedQuantity}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700">
                        {request.origin_on_hand_quantity != null ? request.origin_on_hand_quantity : "—"}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium text-amber-800">
                        {packedQuantity > 0 ? packedQuantity : "—"}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-semibold text-slate-900">
                        {request.requested_quantity}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700">
                        {canMove ? remainder : "—"}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium text-amber-800">
                        {request.inventory_adjustment_quantity != null && request.inventory_adjustment_quantity > 0
                          ? `+${String(request.inventory_adjustment_quantity)}`
                          : "—"}
                      </td>
                      <td className="px-2 py-2 text-slate-700">{request.requested_by_display_name}</td>
                      <td className="px-2 py-2">
                        <div className="flex flex-wrap gap-1">
                          <ReadOnlyHoverLayer
                            active={!hubWritesAllowed}
                            hoverHint="You do not have permission to approve pack quantity discrepancies."
                            layout="inline"
                          >
                            <Button
                              variant="primary"
                              size="sm"
                              disabled={!hubWritesAllowed || mutationPending}
                              onClick={() => approveMutation.mutate(request.id)}
                            >
                              Approve
                            </Button>
                          </ReadOnlyHoverLayer>
                          {canMove ? (
                            <ReadOnlyHoverLayer
                              active={!hubWritesAllowed}
                              hoverHint="You do not have permission to approve and move remainder quantity."
                              layout="inline"
                            >
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={!hubWritesAllowed || mutationPending}
                                onClick={() => {
                                  setMoveDialogRequestId(request.id);
                                  setMoveTargetMode(moveTargetOrders.length > 0 ? "existing" : "new");
                                  setTargetTransferOrderId(
                                    moveTargetOrders[0] != null ? String(moveTargetOrders[0].id) : "",
                                  );
                                }}
                              >
                                Approve and move
                              </Button>
                            </ReadOnlyHoverLayer>
                          ) : null}
                          <ReadOnlyHoverLayer
                            active={!hubWritesAllowed}
                            hoverHint="You do not have permission to reject pack quantity discrepancies."
                            layout="inline"
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={!hubWritesAllowed || mutationPending}
                              onClick={() => {
                                const note =
                                  window.prompt("Optional reason for rejection (or leave blank)") ?? "";
                                rejectMutation.mutate({
                                  requestId: request.id,
                                  note: note.trim() === "" ? null : note.trim(),
                                });
                              }}
                            >
                              Reject
                            </Button>
                          </ReadOnlyHoverLayer>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end border-t border-slate-200 px-4 py-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {moveRequest != null ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-pack-qty-move-title"
        >
          <div className="w-full max-w-lg rounded-lg bg-white p-4 shadow-xl">
            <h2 id="transfer-pack-qty-move-title" className="text-lg font-semibold text-slate-900">
              Approve and move remainder
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Ship {moveRequest.requested_quantity} on this transfer and move{" "}
              {String((lineQuantity(order, moveRequest) ?? moveRequest.ordered_quantity) - moveRequest.requested_quantity)}{" "}
              to another pending transfer on the same lane. The ordered quantity on this line stays unchanged.
            </p>
            <div className="mt-4 space-y-3">
              {moveTargetOrders.length > 0 ? (
                <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
                  <input
                    type="radio"
                    name="approve-move-target-mode"
                    className="mt-1"
                    checked={moveTargetMode === "existing"}
                    onChange={() => setMoveTargetMode("existing")}
                  />
                  <span className="flex-1">
                    <span className="font-medium">Existing pending transfer</span>
                    <select
                      className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      disabled={moveTargetMode !== "existing"}
                      value={targetTransferOrderId}
                      onChange={(event) => setTargetTransferOrderId(event.target.value)}
                    >
                      <option value="">Select transfer…</option>
                      {moveTargetOrders.map((target) => (
                        <option key={target.id} value={String(target.id)}>
                          {target.transfer_number ?? `Transfer #${String(target.id)}`}
                          {target.ship_by_date != null ? ` · ship by ${target.ship_by_date}` : ""}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
              ) : null}
              <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
                <input
                  type="radio"
                  name="approve-move-target-mode"
                  className="mt-1"
                  checked={moveTargetMode === "new"}
                  onChange={() => setMoveTargetMode("new")}
                />
                <span className="flex-1">
                  <span className="font-medium">Create new pending transfer</span>
                  <div className="mt-2 grid gap-2">
                    <div>
                      <Label htmlFor="approve-move-ship-by">Ship by</Label>
                      <Input
                        id="approve-move-ship-by"
                        type="date"
                        disabled={moveTargetMode !== "new"}
                        value={shipByDate}
                        onChange={(event) => setShipByDate(event.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="approve-move-notes">Notes (optional)</Label>
                      <Input
                        id="approve-move-notes"
                        disabled={moveTargetMode !== "new"}
                        value={newTransferNotes}
                        onChange={(event) => setNewTransferNotes(event.target.value)}
                      />
                    </div>
                  </div>
                </span>
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={approveAndMoveMutation.isPending}
                onClick={() => setMoveDialogRequestId(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                isLoading={approveAndMoveMutation.isPending}
                disabled={
                  moveTargetMode === "existing"
                    ? targetTransferOrderId === ""
                    : shipByDate.trim() === ""
                }
                onClick={() => approveAndMoveMutation.mutate(moveRequest.id)}
              >
                Approve and move
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function lineQuantity(
  order: TransferBoardOrder,
  request: { transfer_order_item_id: number; ordered_quantity: number },
): number | null {
  const line = order.lines.find((entry) => entry.id === request.transfer_order_item_id);
  return line?.quantity ?? request.ordered_quantity;
}
